import { useState, useRef } from "react";
import { Loader2, ShieldAlert, RefreshCw, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

/** Returns the backend base URL used by the admin panel. Useful for
 *  ``window.open()`` of download endpoints (PDF/CSV) where we can't use
 *  ``adminFetch``. Cookies flow naturally because the backend is on the
 *  same ``*.emergentagent.com`` eTLD+1 with SameSite=None/Secure. */
export function adminApiBase() {
  return process.env.REACT_APP_BACKEND_URL || "";
}

/** Helper fetch para el panel admin — usa fetch con cookies httpOnly */
export async function adminFetch(url, options = {}) {
  const base = process.env.REACT_APP_BACKEND_URL;  const resp = await fetch(`${base}${url}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  let body = {};
  try { body = await resp.json(); } catch { /* empty */ }
  if (resp.status >= 400) {
    throw new Error(body?.detail || `Error ${resp.status}`);
  }
  return body;
}

/** OTP input de 6 dígitos */
function OtpInput({ value, onChange, disabled }) {
  const DIGITS = 6;
  const refs = Array.from({ length: DIGITS }, () => useRef(null)); // eslint-disable-line
  const digits = value.split("").concat(Array(DIGITS).fill("")).slice(0, DIGITS);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      onChange(value.slice(0, i) + value.slice(i + 1));
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, DIGITS);
    onChange(next);
    if (i < DIGITS - 1) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGITS);
    if (paste) { onChange(paste); refs[Math.min(paste.length, DIGITS - 1)].current?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          value={d} onChange={() => {}}
          onKeyDown={(e) => handleKey(i, e)} onPaste={handlePaste}
          onFocus={(e) => e.target.select()} disabled={disabled}
          data-testid={`otp-modal-digit-${i}`}
          className={`w-10 h-11 text-center text-xl font-bold rounded-lg border-2 bg-slate-800
            focus:outline-none transition-colors
            ${d ? "border-red-500 text-white" : "border-slate-600 text-muted-foreground"}
            ${disabled ? "opacity-50 cursor-not-allowed" : "focus:border-red-400"}`}
        />
      ))}
    </div>
  );
}

/**
 * Modal de confirmación con OTP para acciones destructivas.
 * Props:
 *   open: bool
 *   onClose: fn
 *   onConfirmed: fn(session_id, otp) => Promise  — la acción a ejecutar
 *   title: string
 *   description: string
 */
export function ConfirmOtpModal({ open, onClose, onConfirmed, title, description }) {
  const [step, setStep] = useState("idle"); // idle | sending | waiting | confirming
  const [sessionId, setSessionId] = useState("");
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const reset = () => {
    setStep("idle"); setSessionId(""); setOtp(""); setResendCooldown(0);
    clearInterval(cooldownRef.current);
  };

  const handleClose = () => { reset(); onClose(); };

  const startCooldown = (secs) => {
    setResendCooldown(secs);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const requestOtp = async () => {
    setStep("sending");
    try {
      const data = await adminFetch("/api/admin/action-otp", { method: "POST" });
      setSessionId(data.session_id);
      setStep("waiting");
      startCooldown(60);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.message);
      setStep("idle");
    }
  };

  const handleConfirm = async () => {
    if (otp.length < 6) { toast.error("Introduce los 6 dígitos"); return; }
    setStep("confirming");
    try {
      await onConfirmed(sessionId, otp);
      reset();
      onClose();
    } catch (err) {
      toast.error(err.message);
      setStep("waiting");
      setOtp("");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         data-testid="confirm-otp-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <button onClick={handleClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-slate-300">
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className="w-12 h-12 rounded-xl bg-red-500/15 flex items-center justify-center mb-4">
          <ShieldAlert className="w-6 h-6 text-red-400" />
        </div>

        <h3 className="text-white font-bold text-lg mb-1">{title}</h3>
        <p className="text-slate-400 text-sm mb-5">{description}</p>

        {step === "idle" && (
          <Button onClick={requestOtp}
            data-testid="otp-modal-request-btn"
            className="w-full bg-red-600 hover:bg-red-700 text-white">
            Solicitar código de confirmación
          </Button>
        )}

        {step === "sending" && (
          <Button disabled className="w-full bg-red-600 text-white">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando código...
          </Button>
        )}

        {(step === "waiting" || step === "confirming") && (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-slate-400 text-xs text-center">
                Introduce el código enviado a tu email de administrador
              </p>
              <OtpInput value={otp} onChange={setOtp} disabled={step === "confirming"} />
            </div>

            <Button onClick={handleConfirm}
              disabled={step === "confirming" || otp.length < 6}
              data-testid="otp-modal-confirm-btn"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold">
              {step === "confirming"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Eliminando...</>
                : "Confirmar eliminación"}
            </Button>

            <button onClick={resendCooldown > 0 ? undefined : requestOtp}
              disabled={resendCooldown > 0 || step === "confirming"}
              data-testid="otp-modal-resend-btn"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-slate-300
                         mx-auto transition-colors disabled:opacity-40">
              <RefreshCw className="w-3 h-3" />
              {resendCooldown > 0 ? `Reenviar (${resendCooldown}s)` : "Reenviar código"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
